import { Box, Button, Container, Flex, FormControl, FormErrorMessage, Text, useToast, VStack } from '@chakra-ui/react';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';
import { Formik } from 'formik';
import { BsEnvelope, BsGeoAlt, BsTelephone } from 'react-icons/bs';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import styles from './ContactForm.module.css';
import { ContactFormProps } from './ContactForm.props';
import { InputWithFloatLabel } from '../InputWithFloatLabel';

const ContactForm: FC<ContactFormProps> = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [token, setToken] = useState<string>();
  const toast = useToast();

  const handleReCaptchaVerify = useCallback(async () => {
    if (executeRecaptcha) {
      const newToken = await executeRecaptcha('ApplicationProccess');

      setToken(newToken);
    }
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const handleSendData = (values:any,setSubmitting:any,resetForm:any) => {

    const url = '/api/send-mail';


    const formData= {
      ...values,
      token
    };

    if(token){

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP status ${response.status}`);
          }

          return response;
        })
        .then((response) => response.json())
        .then((resposne) => {
          resetForm();
          toast({
            position:'top',
            title: 'Message send with success!',
            description:'Our staff will get back to you as soon as possible.',
            status: 'success',
            isClosable: true,
          });
        })
        .catch((error) => {
          toast({
            position:'top',
            title: 'Something went wrong when sending email!',
            status: 'error',
            isClosable: true,
          });
        }).finally(() => {
          setSubmitting(false);

        });
    }else{
      toast({
        position:'top',
        title: 'Input are missing or wrong!',
        status: 'error',
        isClosable: true,
      });
      setSubmitting(false);
    }
  };

  const {address,mobile,email,mapIframe} =useSiteSettings();

  return(
    <Box my={{base:'1rem',md:'5rem'}} className={styles.root}>
      <Box py={{base:'3rem', md:'7rem'}} as="section" className={styles.contactForm}>
        <Container maxW={{ base: '1440px', md: '680p' }}>
          <Flex direction={{base:'column', md:'row'}}>
            <Box flex={1}>
              <Box mb={{base:'5rem', md:'0'}} mx={{base:'0', md:'auto'}} maxW="365px">
                <Text mb={{base:'2rem'}} />
                <Flex  className={styles.contactTextWrapper}>
                  <Box className={styles.contactIcons}><BsGeoAlt/></Box>
                  <Text className={styles.contactText}>{address}</Text>
                </Flex>
                <Flex className={styles.contactTextWrapper}>
                  <Box className={styles.contactIcons}><BsTelephone/></Box>
                  <Text className={styles.contactText}>{mobile}</Text>
                </Flex>
                <Flex className={styles.contactTextWrapper}>
                  <Box className={styles.contactIcons}><BsEnvelope/></Box>
                  <Text className={styles.contactText}>{email}</Text>
                </Flex>
              </Box>
            </Box>
            <Box flex={1}>
              <Text as="h3" fontFamily="heading" fontSize={{base:'2xl', md:'4xl'}}>
                Write us
              </Text>
              <Formik
                initialValues={{ email: '', name: '',message:'' }}
                validate={(values:any) => {
                  const errors:any = {};

                  if (!values.message) {
                    errors.message = 'Message is required';
                  }

                  if (!values.name) {
                    errors.name = 'Name is required';
                  }

                  if (!values.email) {
                    errors.email = 'Email is required';
                  } else if (
                    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
                  ) {
                    errors.email = 'Invalid email address';
                  }

                  return errors;
                }}
                onSubmit={(values, { setSubmitting,resetForm }) => {
                  handleSendData(values,setSubmitting,resetForm);
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue
                }) => (
                  <form onSubmit={handleSubmit}>
                    <VStack spacing={5}>
                      <FormControl isInvalid={!!errors.name && !!touched.name}>
                        <InputWithFloatLabel
                          onChange={(e) => setFieldValue('name',e.target.value)}
                          value={values.name}
                          label="Your Name..."
                          id="name"/>
                        {errors.name && touched.name && <FormErrorMessage>{errors.name}</FormErrorMessage>}
                      </FormControl>
                      <FormControl isInvalid={!!errors.email && !!touched.email}>
                        <InputWithFloatLabel
                          onChange={(e) => setFieldValue('email',e.target.value)}
                          value={values.email}  type="email" label="Your Email..." id="email"/>
                        {errors.email && touched.email && <FormErrorMessage>{errors.email}</FormErrorMessage>}
                      </FormControl>
                      <FormControl
                        isInvalid={!!errors.message && !!touched.message}
                        onChange={handleChange}>
                        <InputWithFloatLabel
                          onChange={(e) => setFieldValue('message',e.target.value)}
                          value={values.message}
                          type="textarea"
                          label="Your Message..." id="email"/>
                        {errors.message && touched.message && <FormErrorMessage>{errors.message}</FormErrorMessage>}
                      </FormControl>
                      <FormControl id="name" float="right">
                        <Button
                          disabled={isSubmitting}
                          isLoading={isSubmitting}
                          type="submit"
                          variant="solid"
                          bg="primary"
                          color="white"
                          _hover={{}}>
                            Send Message
                        </Button>
                      </FormControl>
                    </VStack>
                  </form>
                )}
              </Formik>
            </Box>
          </Flex>
        </Container>
      </Box>
      <Box className={styles.mapIframe}>
        <Container maxW={{ base: '1440px', md: '680p' }}>
          <div dangerouslySetInnerHTML={{__html:mapIframe||''}} />
        </Container>
      </Box>
    </Box>
  );
};

const RecaptchaWrapper=(props:any) => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY||''}>
    <ContactForm {...props}/>
  </GoogleReCaptchaProvider>
);

export { RecaptchaWrapper as ContactForm };
